import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, ExternalLink, RefreshCcw, Wallet, TrendingUp, CheckCircle2, AlertCircle } from "lucide-react";

function money(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

/**
 * Host Studio — Payouts panel. Shows Stripe connection status, pending balance,
 * paid-out balance, and per-invoice earnings ledger.
 */
export default function HostPayouts() {
  const [status, setStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, e] = await Promise.all([
        api.get("/host/stripe/status"),
        api.get("/host/earnings"),
      ]);
      setStatus(s.data);
      setEarnings(e.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load payouts");
    }
  };

  useEffect(() => { load(); }, []);

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/host/stripe/onboard");
      if (data.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else if (data.connect_enabled === false) {
        toast.info(data.message || "Connect not yet enabled — earnings still tracked.");
        await load();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Onboarding failed");
    } finally { setBusy(false); }
  };

  const entries = earnings?.entries || [];

  return (
    <div className="surface-card p-6 mt-6" data-testid="host-payouts-panel">
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-[var(--ff-border)]">
        <div className="flex items-center gap-3">
          <Wallet size={20} className="text-[var(--ff-red)]" />
          <div>
            <div className="label-eyebrow text-[var(--ff-red)]">▮ PAYOUTS</div>
            <div className="font-display text-2xl">Your Earnings</div>
            {earnings?.payout_schedule && (
              <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-1" data-testid="host-payout-schedule">
                Paid out {earnings.payout_schedule} · next {new Date(earnings.next_payout_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              </div>
            )}
          </div>
        </div>
        <button onClick={load} className="btn-ghost text-xs" data-testid="host-payouts-refresh">
          <RefreshCcw size={12} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-px bg-[var(--ff-border)] mb-6">
        <StatBox label="Pending" value={money(earnings?.pending_cents)} icon={<TrendingUp size={14} className="text-yellow-300" />} testId="host-pending" />
        <StatBox label="Paid Out" value={money(earnings?.paid_out_cents)} icon={<CheckCircle2 size={14} className="text-green-400" />} testId="host-paidout" />
        <StatBox label="Lifetime" value={money(earnings?.total_cents)} icon={<DollarSign size={14} className="text-white" />} testId="host-lifetime" />
      </div>

      {/* Stripe status */}
      <div className="border border-[var(--ff-border)] p-4 mb-6">
        <div className="label-eyebrow mb-2">▮ STRIPE ACCOUNT</div>
        {status?.linked ? (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm font-mono-ui">
              <div className="text-[var(--ff-text-dim)] uppercase tracking-[0.15em] text-[0.6rem]">Account ID</div>
              <div className="text-white">{status.stripe_account_id}</div>
              <div className="flex gap-2 mt-2">
                <StatusChip ok={status.details_submitted} label="Details submitted" />
                <StatusChip ok={status.charges_enabled} label="Charges enabled" />
                <StatusChip ok={status.payouts_enabled} label="Payouts enabled" />
              </div>
            </div>
            <button onClick={onboard} disabled={busy} className="btn-ghost text-xs" data-testid="host-stripe-manage-btn">
              <ExternalLink size={12} /> {status.charges_enabled ? "Manage on Stripe" : "Finish onboarding"}
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-start gap-3 text-sm mb-4">
              <AlertCircle size={20} className="text-yellow-300 mt-0.5 shrink-0" />
              <div>
                <div className="font-display text-base mb-1">No Stripe account linked yet</div>
                <div className="text-[var(--ff-text-dim)] font-mono-ui text-xs">
                  Link your Stripe account so we can deposit your show earnings directly. Your ledger below is already tracking every payment you&apos;re owed.
                </div>
              </div>
            </div>
            <button onClick={onboard} disabled={busy} className="btn-primary" data-testid="host-stripe-onboard-btn">
              <DollarSign size={14} /> {busy ? "Redirecting…" : "Connect Stripe"}
            </button>
          </div>
        )}
      </div>

      {/* Ledger */}
      <div>
        <div className="label-eyebrow mb-3">▮ EARNINGS LEDGER</div>
        {entries.length === 0 && (
          <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm py-6 text-center" data-testid="host-ledger-empty">
            No earnings yet. Memberships, show merch and donations to you land here and are paid out every Friday.
          </div>
        )}
        {entries.length > 0 && (
          <div className="space-y-1">
            <div className="hidden md:grid grid-cols-[minmax(0,2fr)_90px_minmax(0,1fr)_100px_140px_100px] gap-3 px-3 py-2 text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
              <div>Show</div>
              <div>Source</div>
              <div>Date</div>
              <div>Split</div>
              <div>Your cut</div>
              <div>Status</div>
            </div>
            {entries.map((e) => (
              <div key={e.id} className="grid md:grid-cols-[minmax(0,2fr)_90px_minmax(0,1fr)_100px_140px_100px] gap-3 px-3 py-2 border-t border-[var(--ff-border)] text-sm" data-testid={`host-ledger-row-${e.id}`}>
                <div className="truncate">
                  {e.is_showrunner && <span className="text-[#FFD60A] mr-1" title="Showrunner">★</span>}
                  {e.show_title || "—"}
                </div>
                <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] text-[var(--ff-text-dim)]">{e.source || "membership"}</div>
                <div className="text-[var(--ff-text-dim)] font-mono-ui text-xs">{(e.at || "").slice(0, 10)}</div>
                <div className="font-mono-ui text-xs">{Number(e.split_pct || 0).toFixed(1)}%</div>
                <div className="font-mono-ui">{money(e.host_cut_cents)}</div>
                <div>
                  {e.transferred ? (
                    <span className="px-2 py-0.5 border border-green-500/50 text-green-300 text-[0.55rem] uppercase tracking-[0.15em] font-mono-ui">Paid</span>
                  ) : (
                    <span className="px-2 py-0.5 border border-yellow-500/50 text-yellow-300 text-[0.55rem] uppercase tracking-[0.15em] font-mono-ui" title="Paid out on the next Friday run">Pays Fri</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, icon, testId }) {
  return (
    <div className="bg-[var(--ff-surface)] p-4" data-testid={testId}>
      <div className="label-eyebrow text-[var(--ff-text-dim)] mb-1 flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}

function StatusChip({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono-ui text-[0.55rem] uppercase tracking-[0.15em] ${ok ? "border-green-500/50 text-green-300" : "border-[var(--ff-border)] text-[var(--ff-text-dim)]"}`}>
      {ok ? "✓" : "○"} {label}
    </span>
  );
}
